import { queryOptions } from "@tanstack/react-query";
import { axiosInstance } from "./axios";
import { z } from "zod";

const nn = (schema: z.ZodNumber) => schema.nullable().transform((v) => v ?? 0);

const scoutingPlayerIdentitySchema = z.object({
    teamId: z.number(),
    teamName: z.string(),
    playerId: z.number(),
    playerName: z.string(),
    role: z.string(),
    matchesPlayed: nn(z.number()),
});

const positionKpiDataSchema = z.object({
    position: z.string(),
    avgKpi: nn(z.number()),
});

const scoutingKpiInfoSchema = z.object({
    overall: positionKpiDataSchema.optional().nullable(),
    byPosition: z.array(positionKpiDataSchema).default([]),
});

const scoutingSkatingStatsSchema = z.object({
    topSpeed: nn(z.number()),
    timeOnIce: nn(z.number()),
    distanceTravelled: nn(z.number()),
    averageSpeedWithPuck: nn(z.number()),
    accelerations: nn(z.number()),
    decelerations: nn(z.number()),
});

const scoutingPuckControlStatsSchema = z.object({
    puckControlTime: nn(z.number()),
    puckControlDistance: nn(z.number()),
    puckControlForwardDistance: nn(z.number()),
    puckContestsWon: nn(z.number()),
    puckContestsLost: nn(z.number()),
    puckContestParticipations: nn(z.number()),
    faceOffCount: nn(z.number()),
    faceOffWins: nn(z.number()),
});

const scoutingActionStatsSchema = z.object({
    plus: nn(z.number()),
    minus: nn(z.number()),
    plusMinusTotal: nn(z.number()),
    shots: nn(z.number()),
    goals: nn(z.number()),
    fastestShotSpeed: z.number().nullable(),
    offensiveScreens: nn(z.number()),
    blockedShots: nn(z.number()),
});

const scoutingPlayerPassingStatsSchema = z.object({
    allPassesUnderPressure: nn(z.number()),
    successfulPassesUnderPressure: nn(z.number()),
    allPassesUnderHighPressure: nn(z.number()),
    successfulPassesUnderHighPressure: nn(z.number()),
    averagePressureReceived: nn(z.number()),
    pressuredOpponentsPasses: nn(z.number()),
    averagePressureGiven: nn(z.number()),
    allPasses: nn(z.number()),
    successfulPasses: nn(z.number()),
    receivedPasses: nn(z.number()),
    totalPassDistance: nn(z.number()),
    forwardPassDistance: nn(z.number()),
});

const scoutingAdvancedMetricsSchema = z.object({
    corsiFor: nn(z.number()),
    corsiAgainst: nn(z.number()),
    corsiForPercentage: nn(z.number()),
    relativeCorsiForPercentage: nn(z.number()),
    fenwickForPercentage: nn(z.number()),
    relativeFenwickForPercentage: nn(z.number()),
    fullStrengthSavePercentage: nn(z.number()),
    fullStrengthShootingPercentage: nn(z.number()),
    pdo: nn(z.number()),
});

const scoutingPlayerSchema = z.object({
    identity: scoutingPlayerIdentitySchema,
    kpiInfo: scoutingKpiInfoSchema,
    skatingStats: scoutingSkatingStatsSchema,
    puckControlStats: scoutingPuckControlStatsSchema,
    actionStats: scoutingActionStatsSchema,
    playerPassingStats: scoutingPlayerPassingStatsSchema,
    advancedMetrics: scoutingAdvancedMetricsSchema,
});

const scoutingPlayersResponseSchema = z.object({
    referenceData: scoutingKpiInfoSchema.optional(),
    players: z.array(scoutingPlayerSchema),
});

const scoutingGoalkeeperIdentitySchema = z.object({
    teamId: z.number(),
    teamName: z.string(),
    goalKeeperId: z.number(),
    goalKeeperName: z.string(),
    role: z.string(),
    matchesPlayed: nn(z.number()),
});

const scoutingGoalkeeperPerformanceStatsSchema = z.object({
    timeOnIce: nn(z.number()),
    saves: nn(z.number()),
    goalsAgainst: nn(z.number()),
    goalsAgainstAverage: nn(z.number()),
    savePercentage: nn(z.number()),
});

const scoutingGoalkeeperPassingStatsSchema = z.object({
    successfulPasses: nn(z.number()),
    allPasses: nn(z.number()),
});

const scoutingGoalkeeperWithScreenStatsSchema = z.object({
    shotsAgainstWithScreen: nn(z.number()),
    goalsAgainstWithScreen: nn(z.number()),
});

const scoutingGoalkeeperSchema = z
    .object({
        goalKeeperIdentity: scoutingGoalkeeperIdentitySchema,
        kpiInfoScouting: scoutingKpiInfoSchema,
        performanceStats: scoutingGoalkeeperPerformanceStatsSchema,
        passingStats: scoutingGoalkeeperPassingStatsSchema,
        withScreenStats: scoutingGoalkeeperWithScreenStatsSchema,
    })
    .transform(({ kpiInfoScouting, ...rest }) => ({ ...rest, kpiInfo: kpiInfoScouting }));

const scoutingGoalkeepersResponseSchema = z.object({
    referenceData: scoutingKpiInfoSchema.optional(),
    goalKeepers: z.array(scoutingGoalkeeperSchema),
});

export type PositionKpiData = z.infer<typeof positionKpiDataSchema>;
export type ScoutingKpiInfo = z.infer<typeof scoutingKpiInfoSchema>;
export type ScoutingPlayer = z.infer<typeof scoutingPlayerSchema>;
export type ScoutingPlayersResponse = z.infer<typeof scoutingPlayersResponseSchema>;
export type ScoutingGoalkeeper = z.infer<typeof scoutingGoalkeeperSchema>;
export type ScoutingGoalkeepersResponse = z.infer<typeof scoutingGoalkeepersResponseSchema>;

const fetchScoutingPlayers = async (tournamentId: number): Promise<ScoutingPlayersResponse> => {
    const response = await axiosInstance.get(`/${tournamentId}/scouting/players`);
    return scoutingPlayersResponseSchema.parse(response.data);
};

const fetchScoutingGoalkeepers = async (tournamentId: number): Promise<ScoutingGoalkeepersResponse> => {
    const response = await axiosInstance.get(`/${tournamentId}/scouting/goalkeepers`);
    return scoutingGoalkeepersResponseSchema.parse(response.data);
};

export const scoutingPlayersQueryOptions = (tournamentId: number) =>
    queryOptions({
        queryKey: ["scouting-players", tournamentId],
        queryFn: () => fetchScoutingPlayers(tournamentId),
        staleTime: 5 * 60 * 1000,
    });

export const scoutingGoalkeepersQueryOptions = (tournamentId: number) =>
    queryOptions({
        queryKey: ["scouting-goalkeepers", tournamentId],
        queryFn: () => fetchScoutingGoalkeepers(tournamentId),
        staleTime: 5 * 60 * 1000,
    });

export { scoutingPlayerSchema, scoutingGoalkeeperSchema, scoutingKpiInfoSchema };
